
// Fix: Added missing exported types and interfaces for consistent application state and API responses

export enum Language {
  KURDISH = 'ckb',
  PERSIAN = 'fa'
}

export enum PersonalityType {
  BUTTERFLY = 'BUTTERFLY',
  MOUNTAIN = 'MOUNTAIN',
  LAMP = 'LAMP',
  GARDENER = 'GARDENER'
}

export enum AIEngine {
  GEMINI = 'GEMINI',
  DEEPSEEK = 'DEEPSEEK',
  GROK = 'GROK'
}

export enum ViewState {
  ONBOARDING = 'ONBOARDING',
  DASHBOARD = 'DASHBOARD',
  CHAT = 'CHAT',
  PROFILE = 'PROFILE',
  TIMELINE = 'TIMELINE',
  MODULES = 'MODULES',
  COMPANION = 'COMPANION',
  COMPANION_LIVE = 'COMPANION_LIVE',
  COMMUNITY = 'COMMUNITY',
  PREDICTION = 'PREDICTION',
  BALANCE = 'BALANCE',
  LEARNING = 'LEARNING',
  STUDIO = 'STUDIO',
  IMPACT = 'IMPACT',
  LEGACY = 'LEGACY',
  PHILOSOPHY = 'PHILOSOPHY',
  CALM = 'CALM',
  SOCIAL_TRAINING = 'SOCIAL_TRAINING'
}

export interface ContentItem {
  id: string;
  type: 'quote' | 'joke';
  text: string;
  author?: string;
  category: string;
  timestamp: number;
  rating?: 'like' | 'dislike' | 'love';
}

export interface TimelineEvent {
  id: string;
  type: 'achievement' | 'insight' | 'memory' | 'diary' | 'summary';
  title: string;
  description: string;
  timestamp: number;
  metadata?: any;
}

export interface DiaryEntry {
  id: string;
  timestamp: number;
  transcript: string;
  category: string;
  keywords: string[];
  aiInsight: string;
  mood: string;
}

export interface CommunityRoom {
  id: string;
  name: string;
  description: string;
  members: number;
  icon: string;
}

export interface CommunityPost {
  id: string;
  roomId: string;
  authorName: string;
  authorColor: string;
  content: string;
  timestamp: number;
  likes: number;
  isTrusted: boolean;
}

export interface CompanionMatch {
  id: string;
  name: string;
  compatibility: number;
  interests: string[];
  bio: string;
}

export interface DecisionAnalysis {
  summary: string;
  options: {
    label: string;
    score: number;
    pros: string[];
    cons: string[];
    impact: string;
  }[];
}

export interface PredictiveAlert {
  id: string;
  type: 'health' | 'finance' | 'work' | 'social' | 'general';
  message: string;
  timeframe: string;
  suggestion: string;
  probability: number;
}

export interface MicroClass {
  id: string;
  title: string;
  topic: string;
  content: {
    explanation: string;
    practice: string;
    summary: string;
  };
}

export interface Challenge {
  id: string;
  type: 'mindfulness' | 'gratitude' | 'movement' | 'learning';
  title: string;
  description: string;
  durationDays: number;
  currentDay: number;
  progress: boolean[];
  isActive: boolean;
  startDate: number;
  lastCompletedDate?: string;
}

export interface Badge {
  id: string;
  label: string;
  icon: string;
  dateEarned: number;
  description: string;
  color?: string;
}

export interface VoiceConfig {
  id: string;
  speed: number;
  pitch: number;
}

export interface FeedbackEntry {
  id: string;
  question: string;
  answer: string;
  timestamp: number;
  isImplemented: boolean;
  implementationNote?: string;
}

export interface ImpactReport {
  id: string;
  periodName: string;
  dateGenerated: number;
  metrics: {
    label: string;
    value: string;
    description: string;
    trend: 'up' | 'down' | 'neutral';
  }[];
  aiSummary: string;
}

export interface LegacyWisdom {
  content: string;
  lastUpdated: number;
  sections: {
    lessons: string[];
    mistakes: string[];
    advice: string[];
  };
}

export interface LegacyExecutor {
  name: string;
  relation: string;
  email: string;
  accessCode: string;
  isVerified: boolean;
}

export interface CoreValue {
  id: string;
  label: string;
  description: string;
  icon?: string;
  score: number;
}

export type AnxietyType = 'social' | 'health' | 'work' | 'existential' | 'general';

export interface AnxietyEntry {
  id: string;
  timestamp: number;
  level: number;
  trigger: string;
  type: AnxietyType;
  interventionUsed: string;
}

export interface FearLadderItem {
  id: string;
  fear: string;
  steps: {
    label: string;
    anxietyLevel: number;
    description: string;
  }[];
}

export interface CBTEntry {
  id: string;
  thought: string;
  distortion: string;
  reframing: string;
}

export interface SocialFeedback {
  score: number;
  strengths: string[];
  areasToImprove: string[];
}

export interface SocialMonthlyReport {
  summary: string;
  confidenceProgress: number;
  milestones: string[];
}

export interface WeeklyTask {
  id: string;
  label: string;
  completed: boolean;
}

export interface EventPrepGuide {
  title: string;
  conversationStarters: string[];
  exitStrategies: string[];
  advice: string;
}

export interface UserProfile {
  name: string;
  language: Language;
  country: string; 
  selectedPersonality: PersonalityType;
  preferredEngine: AIEngine;
  level: number;
  xp: number;
  
  stats: {
    physical: number;
    mental: number;
    social: number;
    professional: number;
    financial: number;
  };

  lifeProfile: {
    physical: {
      sleepHours: number;
      energyLevel: number;
      dietaryPreference: string;
      activityLevel: string;
      healthIssues: string[];
    };
    mental: {
      interests: string[];
      learningGoals: string[];
      stressLevel: number;
      mood: string;
    };
    social: {
      socialBattery: number;
      importantRelationsCount: number;
      nextOccasion: string;
      lastSocialInteraction: number;
    };
    professional: {
      skills: string[];
      currentFocus: string;
      burnoutRisk: number;
      careerGoal: string;
    };
    financial: {
      budgetStatus: string;
      financialStress: number;
      savingsGoal: string;
    };
  };

  contentPreferences: {
    favorites: ContentItem[];
    likedCategories: string[];
    dislikedCategories: string[];
    deliveryMode: string;
    jokeFrequency: string;
  };
  timeline: TimelineEvent[];
  diaryEntries: DiaryEntry[];
  chatHistory: ChatMessage[];
  communityProfile: {
    anonymousName: string;
    anonymousColor: string;
    trustScore: number;
    joinedRooms: string[];
    matches: CompanionMatch[];
  };
  appUsage: {
    dailyMinutes: number;
    lastResetDate: string;
  };
  microLearning: {
    activeChallenge: Challenge | null;
    completedClasses: string[];
    badges: Badge[];
    lastClassDate: string;
  };
  immersiveProfile: {
    voice: VoiceConfig;
    music: {
      mood: string;
      volume: number;
      isPlaying: boolean;
      currentTrack: string;
    };
    avatarId: string;
  };
  feedbackSystem: {
    pendingSurveyQuestion: string | null;
    history: FeedbackEntry[];
    reports: ImpactReport[];
    lastSurveyDate: string;
  };
  legacyProfile: {
    wisdom: LegacyWisdom | null;
    postMortemAction: 'delete' | 'archive' | 'memorial';
    executor: LegacyExecutor | null;
    dataRetentionPeriod: number;
  };
  philosophyProfile: {
    coreValues: CoreValue[];
    answeredQuestions: {
      question: string;
      answer: string;
      timestamp: number;
    }[];
    gratitudeJournal: string[];
    activePractice: any;
  };
  anxietyProfile: {
    history: AnxietyEntry[];
    triggers: string[];
    fearLadder: FearLadderItem[];
    cbtHistory: CBTEntry[];
    lastCheckIn: number;
    safetyPlan: {
      warningSigns: string[];
      copingStrategies: string[];
      socialSupports: string[];
      professionalSupports: string[];
    };
  };
  socialTrainingProfile?: {
    level: 'beginner' | 'intermediate' | 'advanced';
    isAssessmentCompleted: boolean;
    completedLessons: string[];
    roleplayHistory: any[];
    weeklyGoal: string;
    confidenceScore: number;
    logs: any[];
    monthlyReports: SocialMonthlyReport[];
    skills: string[];
    badges: string[];
    weeklyPlan: WeeklyTask[];
  };
  traits: string[]; 
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: number;
  isThinking?: boolean;
}
